import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["compareFirst", "compareSecond"]
  static values = { buildSessionId: String }
  
  connect() {
    console.log("Checkpoint management controller connected")
  }
  
  async createManualCheckpoint(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    button.disabled = true
    button.innerHTML = '<span class="loading loading-spinner loading-xs"></span> Creating...'
    
    try {
      const response = await fetch(`/app_projects/${this.buildSessionIdValue}/build_sessions/${this.buildSessionIdValue}/create_checkpoint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          checkpoint_type: 'manual',
          description: 'Manual checkpoint created by user'
        })
      })
      
      if (response.ok) {
        const result = await response.json()
        this.showNotification('Checkpoint created successfully', 'success')
        // Reload the checkpoint list
        window.location.reload()
      } else {
        throw new Error('Failed to create checkpoint')
      }
    } catch (error) {
      console.error('Error creating checkpoint:', error)
      this.showNotification('Failed to create checkpoint', 'error')
    } finally {
      button.disabled = false
      button.innerHTML = '<i class="fas fa-plus mr-1"></i>Create Checkpoint'
    }
  }
  
  async viewCheckpoint(event) {
    event.preventDefault()
    const checkpointId = event.currentTarget.dataset.checkpointId
    
    const modal = document.getElementById('checkpoint-details-modal')
    const content = document.getElementById('checkpoint-details-content')
    
    modal.showModal()
    content.innerHTML = '<div class="flex justify-center py-8"><span class="loading loading-spinner loading-lg"></span></div>'
    
    try {
      const response = await fetch(`/app_projects/${this.buildSessionIdValue}/build_sessions/${this.buildSessionIdValue}/checkpoints/${checkpointId}`, {
        headers: {
          'Accept': 'application/json'
        }
      })
      
      if (response.ok) {
        const checkpoint = await response.json()
        content.innerHTML = this.renderCheckpointDetails(checkpoint)
      } else {
        throw new Error('Failed to load checkpoint details')
      }
    } catch (error) {
      console.error('Error loading checkpoint:', error)
      content.innerHTML = '<div class="alert alert-error">Failed to load checkpoint details</div>'
    }
  }
  
  async restoreCheckpoint(event) {
    event.preventDefault()
    const checkpointId = event.currentTarget.dataset.checkpointId
    
    if (!confirm('Are you sure you want to restore from this checkpoint? This will reset the automation state.')) {
      return
    }
    
    const button = event.currentTarget
    button.disabled = true
    button.innerHTML = '<span class="loading loading-spinner loading-xs"></span>'
    
    try {
      const response = await fetch(`/app_projects/${this.buildSessionIdValue}/build_sessions/${this.buildSessionIdValue}/restore_checkpoint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ checkpoint_id: checkpointId })
      })
      
      if (response.ok) {
        this.showNotification('Checkpoint restored successfully', 'success')
        // Reload the page to show updated state
        setTimeout(() => window.location.reload(), 1500)
      } else {
        throw new Error('Failed to restore checkpoint')
      }
    } catch (error) {
      console.error('Error restoring checkpoint:', error)
      this.showNotification('Failed to restore checkpoint', 'error')
    } finally {
      button.disabled = false
      button.innerHTML = '<i class="fas fa-undo"></i>'
    }
  }
  
  async deleteCheckpoint(event) {
    event.preventDefault()
    const checkpointId = event.currentTarget.dataset.checkpointId
    
    if (!confirm('Are you sure you want to delete this checkpoint?')) {
      return
    }
    
    const button = event.currentTarget
    const row = button.closest('tr')
    
    try {
      const response = await fetch(`/app_projects/${this.buildSessionIdValue}/build_sessions/${this.buildSessionIdValue}/checkpoints/${checkpointId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      
      if (response.ok) {
        // Animate row removal
        row.style.opacity = '0'
        setTimeout(() => row.remove(), 300)
        this.showNotification('Checkpoint deleted', 'success')
      } else {
        throw new Error('Failed to delete checkpoint')
      }
    } catch (error) {
      console.error('Error deleting checkpoint:', error)
      this.showNotification('Failed to delete checkpoint', 'error')
    }
  }
  
  async cleanupCheckpoints(event) {
    event.preventDefault()
    
    if (!confirm('This will remove old and invalid checkpoints. Continue?')) {
      return
    }
    
    try {
      const response = await fetch(`/app_projects/${this.buildSessionIdValue}/build_sessions/${this.buildSessionIdValue}/cleanup_checkpoints`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      
      if (response.ok) {
        const result = await response.json()
        this.showNotification(`Cleaned up ${result.removed} checkpoints`, 'success')
        window.location.reload()
      } else {
        throw new Error('Failed to cleanup checkpoints')
      }
    } catch (error) {
      console.error('Error cleaning up checkpoints:', error)
      this.showNotification('Failed to cleanup checkpoints', 'error')
    }
  }
  
  compareCheckpoints(event) {
    event.preventDefault()
    document.getElementById('compare-checkpoints-modal').showModal()
  }
  
  async performComparison(event) {
    event.preventDefault()
    
    const firstId = this.compareFirstTarget.value
    const secondId = this.compareSecondTarget.value
    
    if (!firstId || !secondId) {
      this.showNotification('Please select two checkpoints to compare', 'warning')
      return
    }
    
    if (firstId === secondId) {
      this.showNotification('Please select different checkpoints', 'warning')
      return
    }
    
    const content = document.getElementById('checkpoint-comparison-content')
    content.innerHTML = '<div class="flex justify-center py-8"><span class="loading loading-spinner loading-lg"></span></div>'
    
    try {
      const response = await fetch(`/app_projects/${this.buildSessionIdValue}/build_sessions/${this.buildSessionIdValue}/compare_checkpoints`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          checkpoint1_id: firstId,
          checkpoint2_id: secondId
        })
      })
      
      if (response.ok) {
        const comparison = await response.json()
        content.innerHTML = this.renderComparison(comparison)
      } else {
        throw new Error('Failed to compare checkpoints')
      }
    } catch (error) {
      console.error('Error comparing checkpoints:', error)
      content.innerHTML = '<div class="alert alert-error">Failed to compare checkpoints</div>'
    }
  }
  
  async exportCheckpoints(event) {
    event.preventDefault()
    
    try {
      const response = await fetch(`/app_projects/${this.buildSessionIdValue}/build_sessions/${this.buildSessionIdValue}/export_checkpoints`, {
        headers: {
          'Accept': 'application/json'
        }
      })
      
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `checkpoints-${this.buildSessionIdValue}-${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        
        this.showNotification('Checkpoints exported successfully', 'success')
      } else {
        throw new Error('Failed to export checkpoints')
      }
    } catch (error) {
      console.error('Error exporting checkpoints:', error)
      this.showNotification('Failed to export checkpoints', 'error')
    }
  }
  
  async loadMore(event) {
    event.preventDefault()
    // This would load more checkpoints via AJAX
    // For now, just reload with a larger limit
    window.location.href = `${window.location.href}?checkpoint_limit=20`
  }
  
  renderCheckpointDetails(checkpoint) {
    return `
      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="label"><span class="label-text font-semibold">Number</span></label>
            <div class="text-lg">#${checkpoint.checkpoint_number}</div>
          </div>
          <div>
            <label class="label"><span class="label-text font-semibold">Type</span></label>
            <div class="badge badge-lg">${checkpoint.checkpoint_type}</div>
          </div>
          <div>
            <label class="label"><span class="label-text font-semibold">Created</span></label>
            <div>${new Date(checkpoint.created_at).toLocaleString()}</div>
          </div>
          <div>
            <label class="label"><span class="label-text font-semibold">Size</span></label>
            <div>${checkpoint.humanized_size}</div>
          </div>
        </div>
        
        ${checkpoint.description ? `
          <div>
            <label class="label"><span class="label-text font-semibold">Description</span></label>
            <div class="p-2 bg-base-200 rounded">${checkpoint.description}</div>
          </div>
        ` : ''}
        
        <div>
          <label class="label"><span class="label-text font-semibold">State Snapshot</span></label>
          <pre class="p-2 bg-base-200 rounded text-xs overflow-x-auto">${JSON.stringify(checkpoint.state_snapshot, null, 2)}</pre>
        </div>
        
        <div>
          <label class="label"><span class="label-text font-semibold">Progress Data</span></label>
          <pre class="p-2 bg-base-200 rounded text-xs overflow-x-auto">${JSON.stringify(checkpoint.progress_data, null, 2)}</pre>
        </div>
      </div>
    `
  }
  
  renderComparison(comparison) {
    return `
      <div class="space-y-4">
        <div class="stats shadow w-full">
          <div class="stat">
            <div class="stat-title">Time Difference</div>
            <div class="stat-value text-2xl">${Math.round(comparison.time_difference / 60)} min</div>
          </div>
          <div class="stat">
            <div class="stat-title">Stories Executed</div>
            <div class="stat-value text-2xl">${comparison.progress_difference.stories_executed}</div>
          </div>
          <div class="stat">
            <div class="stat-title">Completion Change</div>
            <div class="stat-value text-2xl">${comparison.progress_difference.completion_change.toFixed(1)}%</div>
          </div>
        </div>
        
        <div>
          <h4 class="font-semibold mb-2">State Changes</h4>
          <ul class="list-disc list-inside space-y-1">
            ${comparison.state_changes.map(change => `<li>${change}</li>`).join('')}
          </ul>
        </div>
        
        <div>
          <h4 class="font-semibold mb-2">Story Progress</h4>
          <div class="grid grid-cols-3 gap-4">
            <div>
              <span class="text-sm text-base-content/70">Completed:</span>
              <div class="font-mono">+${comparison.stories_difference.completed.length}</div>
            </div>
            <div>
              <span class="text-sm text-base-content/70">Failed:</span>
              <div class="font-mono">+${comparison.stories_difference.failed.length}</div>
            </div>
            <div>
              <span class="text-sm text-base-content/70">Skipped:</span>
              <div class="font-mono">+${comparison.stories_difference.skipped.length}</div>
            </div>
          </div>
        </div>
      </div>
    `
  }
  
  getCSRFToken() {
    return document.querySelector('[name="csrf-token"]').content
  }
  
  showNotification(message, type) {
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} fixed bottom-4 right-4 z-50 max-w-sm`
    toast.innerHTML = `<span>${message}</span>`
    document.body.appendChild(toast)
    
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }
}